import { Component, HostBinding, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CompanyModel } from '../../../models/company.model';
import { CompaniesService } from '../../../services/company.service';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastService } from '../../../services/toast.service';
import { DeleteComponent } from '../../dialogs/delete/delete.component';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ToastrModule } from 'ngx-toastr';
import { KeeniconComponent } from '../../keenicon/keenicon.component';

@Component({
	selector: 'app-header-companies',
	templateUrl: './header-companies.component.html',
	imports: [CommonModule, FormsModule, ReactiveFormsModule, TranslateModule, ToastrModule, KeeniconComponent],
	standalone: true,
})
export class HeaderCompaniesComponent {
	@HostBinding('class') class = 'menu menu-sub menu-sub-dropdown menu-column w-350px w-lg-500px';
	@HostBinding('attr.data-kt-menu') dataKtMenu = 'true';

	@Input() selectedCompany: CompanyModel | null = null;

	readonly dialog = inject(MatDialog) as MatDialog;
	private _subscriptions: Subscription[] = [];

	companies$: BehaviorSubject<CompanyModel[] | null> = new BehaviorSubject<CompanyModel[] | null>(null);
	companies: CompanyModel[] = [];
	search = '';
	private searchTimeout: any;
	private companiesService = inject(CompaniesService) as CompaniesService;
	private translateService = inject(TranslateService) as TranslateService;
	private toastService = inject(ToastService) as ToastService;

	constructor() {
		this.companiesService.firstChangeActiveCompany();
		this._subscriptions.push(
			this.companiesService.companiesChange$.subscribe((res: any) => {
				if (res) {
					this.selectedCompany = this.companiesService.company$.value;
					if (!this.companies.length) this.loadCompanies();
				}
			})
		);

		this._subscriptions.push(
			this.companiesService.company$.subscribe({
				next: (res: any) => {
					if (res?.uuid && (!this.selectedCompany || this.selectedCompany.uuid !== res.uuid)) {
						this.selectedCompany = res;
					}
				},
			})
		);

		this._subscriptions.push(
			this.companiesService.events$.subscribe((r: any) => {
				if (r?.action === this.companiesService.ACTIONS.list) {
					this.companies = r?.payload.data;
					this.companies$.next(this.companies);
					this.verifyActiveCompany();
				} else if (r?.action === this.companiesService.ACTIONS.add || r?.action === this.companiesService.ACTIONS.update || r?.action === this.companiesService.ACTIONS.delete) {
				}
			})
		);
	}

	loadCompanies(): void {
		const params = this.search.trim() ? { search: this.search.trim() } : {};
		this.companiesService.list(params).subscribe({
			next: (res: any) => {
				if (res.data?.length > 0) {
					this.companies = res.data;
					this.companies$.next(this.companies);
				} else {
					this.companiesService.addPopup$.next(true);
				}
			},
			error: () => this.companies$.next(null),
		});
	}

	onSearchInput(): void {
		clearTimeout(this.searchTimeout);
		this.searchTimeout = setTimeout(() => {
			this.loadCompanies();
		}, 300);
	}

	setActiveCompany(company: CompanyModel): void {
		this.selectedCompany = company;
		this.companiesService.setActiveCompany(company);
		this.companiesService.company$.next(company);
	}

	deleteCompany(name: string, uuid: string): void {
		const dialogRef = this.dialog.open(DeleteComponent, {
			width: '500px',
			data: {
				type: this.translateService.instant('the_company'),
				item: name,
				msg: 'the_company_will_no_longer',
			},
		});

		dialogRef.afterClosed().subscribe((result) => {
			if (result) {
				this.companiesService.delete(uuid).subscribe({
					next: () => {
						this.toastService.success('toast_company_eliminated', 'Success');
						this.loadCompanies();
					},
					error: () => this.toastService.error('toast_error_deleting_company', 'Error'),
				});
			} else {
				this.toastService.info('toast_company_not_been_eliminated', 'Info');
			}
		});
	}

	verifyActiveCompany(): void {
		if (this.search) {
			return;
		}
		if (this.companies.length === 0) {
			this.selectedCompany = null;
			this.companiesService.clearCompany();
			return;
		}

		const exists = this.companies.some((c) => c.uuid === this.selectedCompany?.uuid);

		if (!exists) {
			this.selectedCompany = this.companies[0];
			this.companiesService.setActiveCompany(this.selectedCompany);
		}
	}

	openCreateCompany(event?: MouseEvent) {
		if (event) {
			event.stopPropagation();
		}
		this.companiesService.addPopup$.next(true);
	}
}
